/** @jsxImportSource @emotion/react */
import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createStyles, useTheme } from '../../';
import { Button, ButtonProps } from '../Button';
import { Typography } from '../Typography';

interface Props {
  title?: string;
  subtitle?: string;
  children?: ReactNode;
  primaryButtonProps?: Omit<ButtonProps, 'variant'>;
  secondaryButtonProps?: Omit<ButtonProps, 'variant'>;
  closeModal?: () => void;
}

export const Modal: (props: Props) => JSX.Element | null = ({
  title,
  subtitle,
  children,
  primaryButtonProps,
  secondaryButtonProps,
  closeModal,
}: Props) => {
  const [modalRoot, setModalRoot] = useState<HTMLElement>();
  const { palette } = useTheme();
  const styles = useStyles();
  useEffect(() => {
    const root = document.getElementById('modal-root');
    if (!root) {
      throw Error(
        'div [id=modal-root] not found. Have you forgotten DesignSystemProvider'
      );
    }
    setModalRoot(root);
  }, []);

  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <>
      <div css={[styles.spanAll, styles.backdrop]} />
      <div css={styles.spanAll} onClick={() => closeModal && closeModal()}>
        <div css={styles.modal} onClick={(e) => e.stopPropagation()}>
          {title && (
            <div css={styles.title}>
              <Typography variant="subtitle">{title}</Typography>
            </div>
          )}
          {subtitle && (
            <div css={styles.title}>
              <Typography variant="bodySmall" color={palette.grey.x500}>
                {subtitle}
              </Typography>
            </div>
          )}
          <div>{children}</div>
          {(primaryButtonProps || secondaryButtonProps) && (
            <div css={styles.buttonContainer}>
              {secondaryButtonProps && (
                <Button {...secondaryButtonProps} variant="secondary" />
              )}
              {primaryButtonProps && (
                <Button {...primaryButtonProps} variant="primary" />
              )}
            </div>
          )}
        </div>
      </div>
    </>,
    modalRoot
  );
};

const useStyles = createStyles(
  ({ shape, spacing, palette, sizing, breakpoints }) => ({
    spanAll: {
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xxs,
      zIndex: 100,
    },
    backdrop: {
      background: palette.grey.x500,
      opacity: 0.5,
    },
    modal: {
      background: palette.white,
      borderRadius: shape.borderRadius,
      padding: `${spacing.xxs} ${spacing.xxs}`,
      minWidth: sizing.small,
    },
    title: {
      marginBottom: spacing.xxxs,
    },
    buttonContainer: {
      flexDirection: 'column-reverse',
      display: 'flex',
      gap: spacing.xxs,
      marginTop: spacing.xxs,
      [breakpoints.laptopSmall]: {
        flexDirection: 'row',
      },
    },
  })
);
